import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from "formik";
import * as Yup from "yup";
import { Col, Row, Form } from '@themesberg/react-bootstrap';
import axios from "axios";
import { API_URL, END_POINT_ADMIN, END_POINT_ROLES } from "../const";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import EditFormLayout from "../components/EditFormLayout";
import EditFormActions from "../components/EditFormActions";
import VendorPermissionFields from "../components/VendorPermissionFields";
import UserPermissionFields from "../components/UserPermissionFields";
import QuoteViewUsersField from "../components/QuoteViewUsersField";
import AccountStatusField from '../components/AccountStatusField';
import useAuthenticatedAdmin from "../hooks/useAuthenticatedAdmin";
import { hasUserPermission, isSuperAdmin, isVendorOwner } from "../utils/userPermissions";
import { FormikSearchableSelect, buildSelectOptions } from "../components/SearchableSelect";

export const EditAdminForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [accessAllowed, setAccessAllowed] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const { admin: loggedInAdmin, loading: accessLoading } = useAuthenticatedAdmin();

  const [loading, setLoading] = useState(true); // true initially to show loader

  const rawToken = sessionStorage.getItem('quotetoken');
  const token = rawToken?.replace(/^"|"$/g, '');

  const [role, setRole] = useState([]);
  const [teammates, setTeammates] = useState([]);

  const sameVendor = Boolean(loggedInAdmin?.vendor_id && admin?.vendor_id &&
    String(loggedInAdmin.vendor_id) === String(admin.vendor_id));
  const isOwnProfile = String(loggedInAdmin?.id) === String(id);
  const isEditingVendor = isSuperAdmin(loggedInAdmin) && admin && !isSuperAdmin(admin) &&
    admin.is_vendor_owner !== false;
  const canManageVendorPermissions = Boolean(isEditingVendor && admin.vendor);
  const canManageUserPermissions = Boolean(admin && !isOwnProfile && !isSuperAdmin(admin) &&
    !isVendorOwner(admin) && ((isVendorOwner(loggedInAdmin) && sameVendor) ||
      (isSuperAdmin(loggedInAdmin) && admin.is_vendor_owner === false)));
  const canManageVendorStatus = Boolean(canManageVendorPermissions && !isOwnProfile);
  const canManageUserStatus = Boolean(admin && !isOwnProfile && !isSuperAdmin(admin) &&
    !isVendorOwner(admin) && !isEditingVendor && hasUserPermission('users', 'edit', loggedInAdmin) &&
    (isSuperAdmin(loggedInAdmin) || sameVendor));
  const superAdmin = isSuperAdmin(loggedInAdmin);
  const canViewRoles = superAdmin || hasUserPermission('roles', 'view', loggedInAdmin);
  const canChangeRole = !isSuperAdmin(admin) && canViewRoles && hasUserPermission('users', 'edit', loggedInAdmin);

  useEffect(() => {
          if (accessLoading || !canViewRoles || isEditingVendor) return;
          const fetchRolelist = async () => {
            try {
      
              const response = await axios.get(API_URL + (superAdmin ? 'vendor-form/roles' : END_POINT_ROLES), {
                headers: {
                  // 'Content-Type': 'multipart/form-data',
                  'Accept': 'application/json',
                  Authorization: `Bearer ${token}`,
                }
              });
      
              if (response.data.status && Array.isArray(response.data.data)) {
                setRole(response.data.data);
              }
      
            } catch (error) {
              console.error('Error fetching tasks:', error);
            }
      
          };
      
          const timeout = setTimeout(() => {
            fetchRolelist();
          });
          return () => clearTimeout(timeout);
      
        }, [accessLoading, canViewRoles, isEditingVendor, superAdmin, token]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: admin?.name || "",
      email: admin?.email || "",
      phone: admin?.phone || "",
      username: admin?.username || "",
      role_id: admin?.role_id?.toString() || "",
      password: "",
      confirm_password: "",
      allow_multiple_companies: Boolean(admin?.vendor?.allow_multiple_companies),
      permissions: admin?.permissions ?? null,
      is_enabled: admin?.vendor?.is_enabled ?? true,
      is_active: admin?.is_active ?? true,
      quote_view_user_ids: Array.isArray(admin?.quote_view_user_ids) ? admin.quote_view_user_ids.map(Number) : [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      username: Yup.string().required("Username is required"),
      email: Yup.string().email("Invalid email").required("Email is required"),
      phone: Yup.string().matches(/^\d{10}$/, { message: "Phone must be exactly 10 digits", excludeEmptyString: true }),
      role_id: isEditingVendor ? Yup.string() : Yup.string().required("Role is required"),
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .max(10, 'Password must be at most 10 characters')
        .notRequired(),
        confirm_password: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Confirm Password must match Password')
        .when('password', {
          is: (val) => val && val.length > 0,
          then: (schema) => schema.required('Confirm Password is required'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
    onSubmit: async (values, { setErrors }) => {

      if (!accessAllowed) return;
      setSubmitError('');
      const payload = { ...values };
      if (isEditingVendor) delete payload.role_id;
      if (!canManageVendorPermissions) delete payload.allow_multiple_companies;
      if (!canManageVendorStatus || payload.is_enabled === (admin?.vendor?.is_enabled ?? true)) {
        delete payload.is_enabled;
      }
      if (!canManageUserStatus || payload.is_active === (admin?.is_active ?? true)) {
        delete payload.is_active;
      }
      if (!canManageUserPermissions || payload.permissions == null ||
          JSON.stringify(payload.permissions) === JSON.stringify(admin?.permissions)) {
        delete payload.permissions;
      }
      if (canManageUserPermissions) {
        payload.quote_view_user_ids = (values.quote_view_user_ids || []).map(Number);
      } else {
        delete payload.quote_view_user_ids;
      }
      if (!payload.password) {
        delete payload.password;
      }

      try {
        const response = await axios.put(`${API_URL + END_POINT_ADMIN}/${id}`, payload, {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });
        showSuccessToast(response.data.message)
        navigate('/admin');
      } catch (error) {
        const errors = error.response?.data?.errors || {};
        setErrors(Object.fromEntries(Object.entries(errors).map(([field, messages]) =>
          [field, Array.isArray(messages) ? messages.join(' ') : messages])));
        const message = error.response?.data?.message || 'Failed to update user.';
        setSubmitError(message);
        showErrorToast(message);
      }
    },
  });
  
  useEffect(() => {
    if (accessLoading) return;
    let active = true;
    const canRequestEdit = loggedInAdmin?.id && (String(loggedInAdmin.id) === String(id) ||
      hasUserPermission('users', 'edit', loggedInAdmin));
    if (!canRequestEdit) {
      setAccessAllowed(false);
      setLoading(false);
      return;
    }
    setLoading(true);
    const fetchAdminDetails = async () => {
      try {
        const response = await axios.get(API_URL + END_POINT_ADMIN + '/' + id, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
        });
        if (!active) return;
        const data = response.data.data;
        const ownProfile = String(loggedInAdmin.id) === String(data.id);
        const sameTenant = loggedInAdmin.vendor_id && data.vendor_id &&
          String(loggedInAdmin.vendor_id) === String(data.vendor_id);
        const allowed = isSuperAdmin(loggedInAdmin) || ownProfile || Boolean(sameTenant &&
          !isSuperAdmin(data) && !isVendorOwner(data) && hasUserPermission('users', 'edit', loggedInAdmin));
        setAccessAllowed(allowed);
        if (allowed) setAdmin(data);
      } catch (error) {
        if (active) {
          setAccessAllowed(false);
          setSubmitError(error.response?.data?.message || 'Unable to load user details.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchAdminDetails();
    return () => { active = false; };
  }, [id, token, accessLoading, loggedInAdmin]);

  useEffect(() => {
    if (accessLoading || !canManageUserPermissions || !admin?.vendor_id) return;
    let active = true;
    const fetchTeammates = async () => {
      try {
        const params = new URLSearchParams();
        if (isSuperAdmin(loggedInAdmin) && admin.vendor_id) {
          params.set('vendor_id', String(admin.vendor_id));
        }
        const response = await axios.get(`${API_URL}${END_POINT_ADMIN}${params.toString() ? `?${params}` : ''}`, {
          headers: { Accept: 'application/json', Authorization: 'Bearer ' + token },
        });
        if (!active || !Array.isArray(response.data.data)) return;
        setTeammates(response.data.data.filter(user =>
          String(user.id) !== String(admin.id) && !isSuperAdmin(user)));
      } catch (error) {
        if (active) setTeammates([]);
      }
    };
    fetchTeammates();
    return () => { active = false; };
  }, [accessLoading, canManageUserPermissions, admin, loggedInAdmin, token]);

  const canRetainSuperAdmin = isSuperAdmin(loggedInAdmin) && isSuperAdmin(admin);
  const roleOptions = useMemo(() => buildSelectOptions(role.length ? role : (admin?.role ? [admin.role] : []), {
    filter: (item) => String(item.name).trim().toLowerCase() !== 'super admin' ||
      (canRetainSuperAdmin && String(item.id) === String(admin?.role_id)),
  }), [role, admin?.role, admin?.role_id, canRetainSuperAdmin]);

  const usernameField = (
    <Col md={6} className="mb-3">
      <Form.Group controlId="username">
        <Form.Label>Username <span className="text-danger">*</span></Form.Label>
        <Form.Control
          type="text"
          {...formik.getFieldProps("username")}
          isInvalid={formik.touched.username && formik.errors.username}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.username}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  );

  return (
    <EditFormLayout
      title={isEditingVendor ? "Edit Vendor" : "Edit User"}
      subtitle={isEditingVendor ? "Update vendor details and permissions" : "Update user account details"}
      backTo="/admin"
      loading={loading || accessLoading}
    >
      {!accessAllowed ? (
        <div className="text-center py-5 text-danger fw-bold">
          {submitError || "You are not allowed to change this user's details."}
        </div>
      ) : (
        <Form noValidate onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="name">
                  <Form.Label>Name <span className="text-danger">*</span> </Form.Label>
                  <Form.Control
                    type="text"
                    {...formik.getFieldProps("name")}
                    isInvalid={formik.touched.name && formik.errors.name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Group controlId="email">
                  <Form.Label>Email <span className="text-danger">*</span> </Form.Label>
                  <Form.Control
                    type="email"
                    {...formik.getFieldProps("email")}
                    isInvalid={formik.touched.email && formik.errors.email}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.email}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

          <Row>
            <Col md={6} className="mb-3">
              <Form.Group controlId="phone">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  {...formik.getFieldProps("phone")}
                  isInvalid={formik.touched.phone && formik.errors.phone}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.phone}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {isEditingVendor ? usernameField : (
              <Col sm={6} className="mb-3">
                <Form.Group controlId="role_id">
                  <Form.Label>Role <span className="text-danger">*</span></Form.Label>
                  <FormikSearchableSelect
                    formik={formik}
                    name="role_id"
                    options={roleOptions}
                    isDisabled={!canChangeRole}
                    placeholder="Search role..."
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          {!isEditingVendor && <Row>{usernameField}</Row>}

          <Row>
              <Col md={6} className="mb-3">
                <Form.Group controlId="password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    {...formik.getFieldProps("password")}
                    isInvalid={formik.touched.password && formik.errors.password}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.password}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

            <Col md={6} className="mb-3">
              <Form.Group controlId="confirm_password">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  {...formik.getFieldProps("confirm_password")}
                  isInvalid={formik.touched.confirm_password && formik.errors.confirm_password}
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.confirm_password}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          {(canManageVendorStatus || canManageUserStatus) && (
            <AccountStatusField formik={formik} accountType={canManageVendorStatus ? 'vendor' : 'user'} />
          )}
          {canManageVendorPermissions && <VendorPermissionFields formik={formik} />}
          {canManageUserPermissions && <UserPermissionFields formik={formik} existingUser />}
          {canManageUserPermissions && (
            <QuoteViewUsersField
              users={teammates}
              selectedIds={formik.values.quote_view_user_ids}
              onChange={ids => formik.setFieldValue('quote_view_user_ids', ids)}
              disabled={formik.isSubmitting}
            />
          )}
          {submitError && <div role="alert" className="alert alert-danger">{submitError}</div>}

          <EditFormActions
            backTo="/admin"
            isDirty={formik.dirty}
            isSubmitting={formik.isSubmitting}
            saveDisabled={!(formik.dirty && formik.isValid)}
          />
        </Form>
      )}
    </EditFormLayout>
  );
};